import React, { useCallback, useEffect, useState } from "react";
import { X, Loader2, Download, Share2, Copy, CheckCircle2, Linkedin, Twitter, RefreshCw, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, createShareLink, setDesignDiscoverable } from "../lib/api";

/**
 * ShareTransformDialog — V2.0.86 viral share.
 *
 * Composes a "before / after" split-view PNG from:
 *   - Before: the most-recent floor-plan file the user uploaded for this
 *             design (or a placeholder if none exists).
 *   - After: a snapshot of the current 3D viewport, provided by the
 *            parent as a `data:image/png;base64,...` URL. The parent is
 *            responsible for grabbing this from ThreeViewport before
 *            opening the dialog (via `ref.current.toDataURL()`).
 *
 * Props:
 *   open         — bool
 *   designId     — id of the design
 *   snapshotDataUrl — data URL of the 3D viewport snapshot (or null;
 *                     the endpoint returns a placeholder card in that case)
 *   onClose      — () => void
 */
export default function ShareTransformDialog({ open, designId, snapshotDataUrl, onClose }) {
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  // V2.0.89 — Design-owner opt-out toggle. When OFF, this design is
  // hidden from /gallery and /best-transformations feeds. Direct share
  // links (/share/{slug}) still work.
  const [discoverable, setDiscoverable] = useState(true);
  const [savingDiscoverable, setSavingDiscoverable] = useState(false);

  const build = useCallback(async () => {
    if (!designId) return;
    setLoading(true);
    setImageUrl(null);
    setError("");
    try {
      const r = await api.post(`/designs/${designId}/share-transform`, {
        snapshot_data_url: snapshotDataUrl || null,
      });
      setImageUrl(r.data.data_url);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Could not build share image";
      setError(detail);
      // Non-blocking — keep the dialog open with an inline retry button
      // rather than silently closing. Testing agent flagged this on v2.0.86.
    } finally {
      setLoading(false);
    }
  }, [designId, snapshotDataUrl]);

  useEffect(() => {
    if (open) build();
    else { setImageUrl(null); setCopied(false); setError(""); }
  }, [open, build]);

  const download = () => {
    if (!imageUrl) return;
    const a = document.createElement("a");
    a.href = imageUrl;
    a.download = "blueprint-ai-before-after.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const copyPng = async () => {
    if (!imageUrl) return;
    try {
      const blob = await (await fetch(imageUrl)).blob();
      if (!navigator.clipboard?.write) throw new Error("clipboard-write unavailable");
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
      setCopied(true);
      toast.success("PNG copied to clipboard — paste it anywhere");
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      toast.error("Copy failed — use Download instead");
    }
  };

  // V2.0.89 — LinkedIn caches OG previews aggressively. Give users a
  // one-click "refresh preview" that opens LinkedIn's post inspector
  // for the current /share/{slug}. LinkedIn will re-crawl and update.
  // V2.0.90 — Rate-limited to 1x/slug/hour via sessionStorage so users
  // don't accidentally hammer LinkedIn's Post Inspector (they'll IP-ban).
  const refreshLinkedInPreview = async () => {
    try {
      const r = await createShareLink(designId);
      const slug = r.data?.share_slug;
      if (!slug) throw new Error("no share slug");
      const throttleKey = `bp_li_refresh_${slug}`;
      const last = parseInt(sessionStorage.getItem(throttleKey) || "0", 10);
      if (Date.now() - last < 60 * 60 * 1000) {
        const mins = Math.ceil((60 * 60 * 1000 - (Date.now() - last)) / 60000);
        toast("LinkedIn re-crawl already requested. Try again in " + mins + " min.");
        return;
      }
      const crawlableUrl = `${window.location.origin}/api/share/${slug}/social-html`;
      const inspector = `https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(crawlableUrl)}`;
      window.open(inspector, "_blank", "noopener,noreferrer");
      sessionStorage.setItem(throttleKey, String(Date.now()));
      toast.success("LinkedIn Post Inspector opened — click 'Inspect' to force a re-crawl");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Could not open Post Inspector");
    }
  };

  // V2.0.90 — Twitter Cards validator. Same pattern as LinkedIn, no
  // throttle needed (Twitter's validator is public/unlimited).
  const validateTwitterCard = async () => {
    try {
      const r = await createShareLink(designId);
      const slug = r.data?.share_slug;
      if (!slug) throw new Error("no share slug");
      const crawlableUrl = `${window.location.origin}/api/share/${slug}/social-html`;
      const validator = `https://cards-dev.twitter.com/validator?url=${encodeURIComponent(crawlableUrl)}`;
      window.open(validator, "_blank", "noopener,noreferrer");
      toast.success("Twitter Card Validator opened");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Could not open validator");
    }
  };

  const share = async () => {
    if (!imageUrl) return;
    try {
      const blob = await (await fetch(imageUrl)).blob();
      const file = new File([blob], "blueprint-ai.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Blueprint AI — Sketch to 3D" });
      } else {
        download();
      }
    } catch (err) {
      download();
    }
  };

  // V2.0.87 — LinkedIn / X share intents. These open the platform's
  // share dialog with a link back to the public /share/{slug} page.
  // The user still needs to download + attach the composed PNG manually
  // (LinkedIn/X don't accept data URLs in intent params), but the
  // pre-filled link makes the post feel effortless.
  const shareToNetwork = async (network) => {
    try {
      const r = await createShareLink(designId);
      const slug = r.data?.share_slug;
      if (!slug) throw new Error("no share slug");
      const shareUrl = `${window.location.origin}/share/${slug}`;
      // V2.0.88 — Twitter / Facebook / Slack crawlers don't execute JS
      // and therefore miss the react-helmet-async OG tags. We share
      // `/api/share/{slug}/social-html` — a server-rendered HTML shell
      // with baked og:* tags AND a `<meta refresh>` that forwards
      // human visitors to the SPA. Best of both worlds.
      const crawlableUrl = `${window.location.origin}/api/share/${slug}/social-html`;
      const text = "Just turned my sketch into a real 3D home with Blueprint AI. Check it out:";
      let target = "";
      if (network === "linkedin") {
        target = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(crawlableUrl)}`;
      } else if (network === "twitter") {
        target = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(crawlableUrl)}`;
      }
      if (!target) return;
      // Auto-download the composed PNG so the user has it ready to
      // attach in the network's compose window.
      download();
      window.open(target, "_blank", "noopener,noreferrer");
      toast.success(`${network === "linkedin" ? "LinkedIn" : "X"} opened — attach the downloaded PNG`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Could not open share link");
    }
  };

  if (!open) return null;
  return (
    <div
      data-testid="share-transform-dialog"
      className="fixed inset-0 z-50 grid place-items-center bg-zinc-950/80 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-3xl bg-zinc-950 border border-brass-500/40">
        <header className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 mb-1">
              // Before / After
            </div>
            <h2 className="font-display text-xl text-zinc-50">Share your transformation</h2>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100 p-1" data-testid="share-transform-close">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="p-6 space-y-4">
          <div className="bg-zinc-900 border border-zinc-800 grid place-items-center aspect-[16/9] overflow-hidden">
            {loading && (
              <div className="flex flex-col items-center gap-2 text-zinc-500">
                <Loader2 className="w-8 h-8 text-brass-400 animate-spin" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400">
                  Composing image…
                </div>
              </div>
            )}
            {!loading && !imageUrl && error && (
              <div className="flex flex-col items-center gap-3 text-zinc-400 max-w-md text-center px-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-rose-400">
                  // Composition failed
                </div>
                <div className="font-body text-sm leading-relaxed">{error}</div>
                <button
                  type="button"
                  onClick={build}
                  className="inline-flex items-center gap-1.5 border border-brass-700/60 bg-brass-950/20 hover:bg-brass-900/40 text-brass-200 font-mono text-[10px] uppercase tracking-widest px-4 py-2"
                  data-testid="share-transform-retry"
                >
                  <Loader2 className="w-3.5 h-3.5" /> Retry
                </button>
              </div>
            )}
            {!loading && imageUrl && (
              <img
                src={imageUrl}
                alt="Before and after: your sketch next to the AI-built 3D render"
                data-testid="share-transform-image"
                className="w-full h-full object-contain"
              />
            )}
          </div>
          <div className="font-body text-xs text-zinc-500 leading-relaxed border-l-2 border-brass-500/40 pl-3">
            Post this on LinkedIn or X to show off what Blueprint AI turned your sketch into.
            The image is 1600 × 900 — perfect for social previews.
          </div>
          {/* V2.0.89 — Discoverable opt-out. Direct /share/{slug} links
              always work; this only controls whether the design appears
              in the public /gallery + /best-transformations feeds. */}
          <label
            data-testid="share-transform-discoverable"
            className="flex items-center gap-3 border border-zinc-800 hover:border-brass-700/60 bg-zinc-900/40 px-4 py-2.5 cursor-pointer transition-colors"
          >
            <input
              type="checkbox"
              checked={discoverable}
              onChange={async (e) => {
                const next = e.target.checked;
                setDiscoverable(next);
                setSavingDiscoverable(true);
                try {
                  await setDesignDiscoverable(designId, next);
                  toast.success(next ? "Design will appear in the public gallery" : "Design hidden from public gallery");
                } catch (err) {
                  setDiscoverable(!next);
                  toast.error(err?.response?.data?.detail || "Could not update setting");
                } finally {
                  setSavingDiscoverable(false);
                }
              }}
              disabled={savingDiscoverable}
              data-testid="share-transform-discoverable-checkbox"
              className="accent-brass-500 w-4 h-4 shrink-0"
            />
            <div className="flex-1">
              <div className="font-mono text-[10px] uppercase tracking-widest text-brass-300">
                // List in public gallery
              </div>
              <div className="text-zinc-500 text-[11px] mt-0.5">
                {discoverable
                  ? "This design shows up on /gallery and /best-transformations."
                  : "Hidden from public feeds. Your direct share link still works."}
              </div>
            </div>
            {savingDiscoverable ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-brass-400" />
            ) : (
              <EyeOff className={`w-3.5 h-3.5 ${discoverable ? "text-zinc-600" : "text-brass-400"}`} />
            )}
          </label>
        </div>
        <footer className="flex items-center justify-end gap-2 px-6 py-4 border-t border-zinc-800 bg-zinc-900/40 flex-wrap">
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-zinc-100 px-4 py-2"
            data-testid="share-transform-cancel"
          >
            Close
          </button>
          <button
            type="button"
            onClick={copyPng}
            disabled={loading || !imageUrl}
            className="inline-flex items-center gap-1.5 border border-brass-700/60 bg-brass-950/20 hover:bg-brass-900/40 text-brass-200 font-mono text-[10px] uppercase tracking-widest px-4 py-2 disabled:opacity-40"
            data-testid="share-transform-copy"
          >
            {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "Copied" : "Copy PNG"}
          </button>
          <button
            type="button"
            onClick={download}
            disabled={loading || !imageUrl}
            className="inline-flex items-center gap-1.5 border border-brass-700/60 bg-brass-950/20 hover:bg-brass-900/40 text-brass-200 font-mono text-[10px] uppercase tracking-widest px-4 py-2 disabled:opacity-40"
            data-testid="share-transform-download"
          >
            <Download className="w-3.5 h-3.5" />
            Download
          </button>
          <button
            type="button"
            onClick={() => shareToNetwork("linkedin")}
            disabled={loading || !imageUrl}
            className="inline-flex items-center gap-1.5 border border-[#0A66C2]/60 bg-[#0A66C2]/10 hover:bg-[#0A66C2]/25 text-[#4E9BE0] font-mono text-[10px] uppercase tracking-widest px-4 py-2 disabled:opacity-40"
            data-testid="share-transform-linkedin"
            title="Open LinkedIn share window with a link back to your design"
          >
            <Linkedin className="w-3.5 h-3.5" />
            LinkedIn
          </button>
          <button
            type="button"
            onClick={refreshLinkedInPreview}
            disabled={loading || !imageUrl}
            className="inline-flex items-center gap-1.5 border border-[#0A66C2]/40 bg-transparent hover:bg-[#0A66C2]/10 text-[#4E9BE0]/80 font-mono text-[10px] uppercase tracking-widest px-3 py-2 disabled:opacity-40"
            data-testid="share-transform-linkedin-refresh"
            title="Force LinkedIn to re-crawl your design's OG preview via the Post Inspector"
          >
            <RefreshCw className="w-3 h-3" />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => shareToNetwork("twitter")}
            disabled={loading || !imageUrl}
            className="inline-flex items-center gap-1.5 border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-200 font-mono text-[10px] uppercase tracking-widest px-4 py-2 disabled:opacity-40"
            data-testid="share-transform-twitter"
            title="Open X (Twitter) share window with a link back to your design"
          >
            <Twitter className="w-3.5 h-3.5" />
            X
          </button>
          <button
            type="button"
            onClick={validateTwitterCard}
            disabled={loading || !imageUrl}
            className="inline-flex items-center gap-1.5 border border-zinc-700 bg-transparent hover:bg-zinc-800 text-zinc-400 font-mono text-[10px] uppercase tracking-widest px-3 py-2 disabled:opacity-40"
            data-testid="share-transform-twitter-validator"
            title="Open Twitter Card Validator to preview how X will render this share"
          >
            <ExternalLink className="w-3 h-3" />
            Validate
          </button>
          <button
            type="button"
            onClick={share}
            disabled={loading || !imageUrl}
            className="inline-flex items-center gap-1.5 bg-orange-600 hover:bg-orange-500 text-white font-mono text-[10px] uppercase tracking-widest px-4 py-2 disabled:opacity-50"
            data-testid="share-transform-share"
          >
            <Share2 className="w-3.5 h-3.5" />
            Share
          </button>
        </footer>
      </div>
    </div>
  );
}
